import { cloneElement, isValidElement, useMemo } from "react";
import { format } from "date-fns";
import { Ellipsis } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Column, ColumnDataType, DataTableFilterActions, FilterModel } from "../core/types";
import { take } from "../lib/array";
import { type Locale, t } from "../lib/i18n";

interface FilterValueDisplayProps<TData, TType extends ColumnDataType> {
  filter: FilterModel<TType>;
  column: Column<TData, TType>;
  actions: DataTableFilterActions;
  locale?: Locale;
}

export function FilterValueDisplay<TData, TType extends ColumnDataType>({
  filter,
  column,
  actions,
  locale = "en",
}: FilterValueDisplayProps<TData, TType>) {
  switch (column.type) {
    case "option":
      return (
        <FilterValueOptionDisplay
          filter={filter as FilterModel<"option">}
          column={column as Column<TData, "option">}
          actions={actions}
          locale={locale}
        />
      );
    case "multiOption":
      return (
        <FilterValueMultiOptionDisplay
          filter={filter as FilterModel<"multiOption">}
          column={column as Column<TData, "multiOption">}
          actions={actions}
          locale={locale}
        />
      );
    case "date":
      return (
        <FilterValueDateDisplay
          filter={filter as FilterModel<"date">}
          column={column as Column<TData, "date">}
          actions={actions}
          locale={locale}
        />
      );
    case "text":
      return (
        <FilterValueTextDisplay
          filter={filter as FilterModel<"text">}
          column={column as Column<TData, "text">}
          actions={actions}
          locale={locale}
        />
      );
    case "number":
      return (
        <FilterValueNumberDisplay
          filter={filter as FilterModel<"number">}
          column={column as Column<TData, "number">}
          actions={actions}
          locale={locale}
        />
      );
    default:
      return null;
  }
}

export function FilterValueOptionDisplay<TData>({
  filter,
  column,
  actions: _,
  locale: __ = "en",
}: FilterValueDisplayProps<TData, "option">) {
  const options = useMemo(() => column.getOptions(), [column]);
  const selected = options.filter((o) => filter?.values.includes(o.value));

  // We display the selected options based on how many are selected
  //
  // If there is only one option selected, we display its icon and label
  //
  // If there are multiple options selected, we display:
  // 1) up to 3 icons of the selected options
  // 2) the number of selected options
  if (selected.length === 1) {
    const { label, icon: Icon } = selected[0];
    const hasIcon = !!Icon;
    return (
      <span className="inline-flex items-center gap-1">
        {hasIcon && (isValidElement(Icon) ? Icon : <Icon className="size-4 text-primary" />)}
        <span>{label}</span>
      </span>
    );
  }
  const name = column.displayName.toLowerCase();
  // TODO: Better pluralization for different languages
  const pluralName = name.endsWith("s") ? `${name}es` : `${name}s`;

  const hasOptionIcons = !options?.some((o) => !o.icon);

  return (
    <div className="inline-flex items-center gap-0.5">
      {hasOptionIcons &&
        take(selected, 3).map(({ value, icon }) => {
          const Icon = icon!;
          return isValidElement(Icon) ? Icon : <Icon key={value} className="size-4" />;
        })}
      <span className={cn(hasOptionIcons && "ml-1.5")}>
        {selected.length} {pluralName}
      </span>
    </div>
  );
}

export function FilterValueMultiOptionDisplay<TData>({
  filter,
  column,
  actions: _,
  locale: __ = "en",
}: FilterValueDisplayProps<TData, "multiOption">) {
  const options = useMemo(() => column.getOptions(), [column]);
  const selected = options.filter((o) => filter.values.includes(o.value));

  if (selected.length === 1) {
    const { label, icon: Icon } = selected[0];
    const hasIcon = !!Icon;
    return (
      <span className="inline-flex items-center gap-1.5">
        {hasIcon && (isValidElement(Icon) ? Icon : <Icon className="size-4 text-primary" />)}

        <span>{label}</span>
      </span>
    );
  }

  const name = column.displayName.toLowerCase();

  const hasOptionIcons = !options?.some((o) => !o.icon);

  return (
    <div className="inline-flex items-center gap-1.5">
      {hasOptionIcons && (
        <div key="icons" className="inline-flex items-center gap-0.5">
          {take(selected, 3).map(({ value, icon }) => {
            const Icon = icon!;
            return isValidElement(Icon) ? cloneElement(Icon, { key: value }) : <Icon key={value} className="size-4" />;
          })}
        </div>
      )}
      <span>
        {selected.length} {name}
      </span>
    </div>
  );
}

function formatDateRange(start: Date, end: Date) {
  const sameMonth = new Date(start).getMonth() === new Date(end).getMonth();
  const sameYear = new Date(start).getFullYear() === new Date(end).getFullYear();

  if (sameMonth && sameYear) {
    return `${format(start, "MMM d")} - ${format(end, "d, yyyy")}`;
  }

  if (sameYear) {
    return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
  }

  return `${format(start, "MMM d, yyyy")} - ${format(end, "MMM d, yyyy")}`;
}

export function FilterValueDateDisplay<TData>({
  filter,
  column: _,
  actions: __,
  locale: ___ = "en",
}: FilterValueDisplayProps<TData, "date">) {
  if (!filter) {
    return null;
  }
  if (filter.values.length === 0) {
    return <Ellipsis className="size-4" />;
  }
  if (filter.values.length === 1) {
    const value = filter.values[0];

    const formattedDateStr = format(value, "MMM d, yyyy");

    return <span>{formattedDateStr}</span>;
  }

  const formattedRangeStr = formatDateRange(filter.values[0], filter.values[1]);

  return <span>{formattedRangeStr}</span>;
}

export function FilterValueTextDisplay<TData>({
  filter,
  column: _,
  actions: __,
  locale: ___ = "en",
}: FilterValueDisplayProps<TData, "text">) {
  if (!filter) {
    return null;
  }
  if (filter.values.length === 0 || filter.values[0].trim() === "") {
    return <Ellipsis className="size-4" />;
  }

  const value = filter.values[0];

  return <span>{value}</span>;
}

export function FilterValueNumberDisplay<TData>({
  filter,
  column: _,
  actions: __,
  locale = "en",
}: FilterValueDisplayProps<TData, "number">) {
  if (!filter || !filter.values || filter.values.length === 0) {
    return null;
  }

  if (filter.operator === "is between" || filter.operator === "is not between") {
    const minValue = filter.values[0];
    const maxValue = filter.values[1];

    return (
      <span className="tabular-nums tracking-tight">
        {minValue} {t("and", locale)} {maxValue}
      </span>
    );
  }

  const value = filter.values[0];
  return <span className="tabular-nums tracking-tight">{value}</span>;
}
